import React, { useEffect, useMemo, useState, useRef, Children } from 'react';
import { Select } from '../../ui/Select';
import { Input } from '../../ui/Input';
import { Callout } from '../../ui/Callout';
import { ROMANIAN_CITIES, LOCATION_TREE } from '../../../lib/constants';
import { OnboardingDraft } from './types';
import { X, MagnifyingGlass, CaretDown, CaretRight, Check } from '@phosphor-icons/react';
import { cn } from '../../../lib/utils';
interface StepLocationBudgetProps {
  data: Partial<OnboardingDraft>;
  updateData: (data: Partial<OnboardingDraft>) => void;
}
export function StepLocationBudget({
  data,
  updateData
}: StepLocationBudgetProps) {
  const [search, setSearch] = useState('');
  const [showDropdown, setShowDropdown] = useState(false);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const selected = data.neighborhoods || [];
  // Flatten tree for search across all cities
  const flatNodes = useMemo(() => {
    const flat: any[] = [];
    const traverse = (nodes: any[]) => {
      nodes.forEach(n => {
        flat.push(n);
        if (n.children) traverse(n.children);
      });
    };
    traverse(LOCATION_TREE as any);
    return flat;
  }, []);
  const searchResults = useMemo(() => {
    if (!search.trim()) return null;
    const q = search.toLowerCase();
    return flatNodes.filter(n => n.label.toLowerCase().includes(q));
  }, [search, flatNodes]);
  const [expandedNodes, setExpandedNodes] = useState<Set<string>>(new Set(['București']));
  const dropdownRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setShowDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);
  const toggleExpand = (e: React.MouseEvent, value: string) => {
    e.stopPropagation();
    setExpandedNodes(prev => {
      const next = new Set(prev);
      if (next.has(value)) next.delete(value);else next.add(value);
      return next;
    });
  };
  const addNeighborhood = (node: any) => {
    // Only add if it's a leaf node (neighborhood) or a sector, and we have space
    if (selected.length >= 3 || selected.includes(node.value)) return;
    updateData({
      neighborhoods: [...selected, node.value]
    });
    setSearch('');
    setShowDropdown(false);
  };
  const removeNeighborhood = (zone: string) => {
    updateData({
      neighborhoods: selected.filter(z => z !== zone)
    });
  };
  const renderNode = (node: any, depth = 0) => {
    const isExpanded = expandedNodes.has(node.value);
    const hasChildren = !!node.children;
    const isSelected = selected.includes(node.value);
    return <div key={node.value} data-id="element-1670">
        <div className={cn('flex items-center gap-2 px-3 py-1.5 rounded-lg cursor-pointer group transition-colors', isSelected ? 'bg-dark-3' : 'hover:bg-dark-3')} style={{
        paddingLeft: `${depth * 16 + 12}px`
      }} onClick={() => addNeighborhood(node)} data-id="element-1671">
          {hasChildren ? <button type="button" onClick={e => toggleExpand(e, node.value)} className="w-5 h-5 flex items-center justify-center text-light-4 hover:text-light-1 shrink-0" data-id="element-1672">
              <CaretRight size={14} className={cn('transition-transform', isExpanded && 'rotate-90')} data-id="element-1673" />
            </button> : <div className="w-5 shrink-0" data-id="element-1674" />}

          <div className={cn('w-4 h-4 rounded-md border flex items-center justify-center shrink-0 transition-colors', isSelected ? 'bg-lime-1 border-lime-1 text-dark-1' : 'border-light-3')} data-id="element-1675">
            {isSelected && <Check size={12} weight="bold" data-id="element-1676" />}
          </div>

          <span className={cn('text-sm truncate', depth === 0 ? 'text-light-1 font-medium' : 'text-light-2')} data-id="element-1677">
            {node.label}
          </span>
        </div>

        {hasChildren && isExpanded && <div className="mt-0.5" data-id="element-1678">
            {node.children.map((child: any) => renderNode(child, depth + 1))}
          </div>}
      </div>;
  };
  // Popular suggestions (top level children of București for quick access)
  const popularSuggestions = useMemo(() => {
    const bucNode = (LOCATION_TREE as any[]).find(n => n.value === 'București');
    if (!bucNode || !bucNode.children) return [];
    return bucNode.children.slice(0, 6);
  }, []);
  return <div className="space-y-8 animate-in fade-in slide-in-from-bottom-4 duration-500" data-id="element-1679">
      <div data-id="element-1680">
        <h2 className="text-2xl font-bold text-light-1" data-id="element-1681">
          Unde și cu ce buget?
        </h2>
      </div>

      <div className="space-y-6" data-id="element-1682">
        <div className="space-y-2" data-id="element-1683">
          <label className="block text-sm font-medium text-light-2" data-id="element-1684">
            Locație
          </label>
          <p className="text-xs text-light-3 mb-2" data-id="element-1685">
            Alege până la 3 zone preferate (oraș, sector sau cartier).
          </p>

          {/* Selected chips */}
          {selected.length > 0 && <div className="flex flex-wrap gap-2 mb-3" data-id="element-1686">
              {selected.map(zone => {
            // Extract just the last part for display
            const displayLabel = zone.split('|').pop();
            return <div key={zone} className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-lime-4 border border-lime-1 text-sm text-lime-1" data-id="element-1687">
                    {displayLabel}
                    <button onClick={() => removeNeighborhood(zone)} className="text-lime-3 hover:text-lime-1" type="button" data-id="element-1688">
                      <X className="w-3 h-3" weight="bold" data-id="element-1689" />
                    </button>
                  </div>;
          })}
            </div>}

          {/* Search input with dropdown */}
          {selected.length < 3 && <div className="relative" ref={dropdownRef} data-id="element-1690">
              <button type="button" onClick={() => setShowDropdown(v => !v)} className={cn('w-full h-11 px-4 rounded-md text-sm flex items-center justify-between transition-colors cursor-pointer', 'bg-dark-2 border border-dark-3 text-light-3 hover:border-dark-4')} data-id="element-1691">
                <span className="truncate pr-2 text-left" data-id="element-1692">
                  Caută oraș, sector, zonă...
                </span>
                <CaretDown size={14} weight="bold" className={cn('shrink-0 text-light-3 transition-transform', showDropdown && 'rotate-180')} data-id="element-1693" />
              </button>

              {showDropdown && <div className="absolute top-full left-0 right-0 mt-2 bg-dark-2 border border-white/15 rounded-xl z-50 overflow-hidden animate-in fade-in slide-in-from-top-2 flex flex-col max-h-[400px]" data-id="element-1694">
                  <div className="p-3 border-b border-white/10 shrink-0" data-id="element-1695">
                    <div className="relative" data-id="element-1696">
                      <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-light-4" data-id="element-1697" />
                      <input type="text" value={search} onChange={e => setSearch(e.target.value)} placeholder="Caută oraș, sector, zonă..." className="w-full h-9 bg-dark-3 border border-dark-4 rounded-lg pl-9 pr-3 text-sm text-light-1 placeholder:text-light-4 focus:outline-none focus:border-lime-1 focus:ring-1 focus:ring-lime-1" data-id="element-1698" />
                    </div>
                  </div>

                  <div className="flex-1 overflow-y-auto p-2 dark-scrollbar" data-id="element-1699">
                    {searchResults ? searchResults.length > 0 ? <div className="space-y-1" data-id="element-1700">
                          {searchResults.map(node => {
                  const isSelected = selected.includes(node.value);
                  return <button key={node.value} type="button" onClick={() => addNeighborhood(node)} className={cn('w-full flex items-center gap-3 px-3 py-2 rounded-lg transition-colors text-left', isSelected ? 'bg-dark-3' : 'hover:bg-dark-3')} data-id="element-1701">
                                <div className={cn('w-4 h-4 rounded-md border flex items-center justify-center shrink-0 transition-colors', isSelected ? 'bg-lime-1 border-lime-1 text-dark-1' : 'border-light-3')} data-id="element-1702">
                                  {isSelected && <Check size={12} weight="bold" data-id="element-1703" />}
                                </div>
                                <div className="min-w-0" data-id="element-1704">
                                  <div className="text-sm text-light-1 font-medium truncate" data-id="element-1705">
                                    {node.label}
                                  </div>
                                  <div className="text-xs text-light-4 truncate" data-id="element-1706">
                                    {node.value.replace(/\|/g, ' · ')}
                                  </div>
                                </div>
                              </button>;
                })}
                        </div> : <div className="py-8 text-center text-sm text-light-4" data-id="element-1707">
                          Nu am găsit rezultate pentru „{search}"
                        </div> : <div className="space-y-1" data-id="element-1708">
                        {(LOCATION_TREE as any[]).map((node: any) => renderNode(node))}
                      </div>}
                  </div>
                </div>}
            </div>}

          {/* Quick-add chips for empty state */}
          {selected.length < 3 && !showDropdown && popularSuggestions.length > 0 && <div className="mt-3" data-id="element-1709">
                <p className="text-xs text-light-3 mb-2" data-id="element-1710">Sugestii populare:</p>
                <div className="flex flex-wrap gap-2" data-id="element-1711">
                  {popularSuggestions.filter((n: any) => !selected.includes(n.value)).map((node: any) => <button key={node.value} type="button" onClick={() => addNeighborhood(node)} className="px-3 py-1.5 rounded-full text-sm font-medium border border-dark-3 bg-dark-2 text-light-3 hover:border-dark-4 hover:text-light-2 transition-colors" data-id="element-1712">
                        + {node.label}
                      </button>)}
                </div>
              </div>}
        </div>

        <div className="space-y-2 pt-4" data-id="element-1713">
          <label className="block text-sm font-medium text-light-2" data-id="element-1714">
            Cât ești dispus să dai pe lună?
          </label>
          <div className="relative" data-id="element-1715">
            <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none z-10" data-id="element-1716">
              <span className="text-xl font-bold text-lime-1" data-id="element-1717">€</span>
            </div>
            <input type="number" inputMode="numeric" value={data.budgetEur || ''} onChange={e => updateData({
            budgetEur: parseInt(e.target.value) || undefined
          })} className="flex h-14 w-full rounded-md border border-dark-3 bg-dark-2 pl-10 pr-3 py-2 text-lg font-semibold text-light-1 placeholder:text-light-3 focus:outline-none focus:border-lime-1 transition-colors" placeholder="ex: 450" data-id="element-1718" />
          </div>
          <Callout variant="tip" className="mt-2" data-id="element-1719">
            Pune suma maximă, noi îți aducem și ofertele mai ieftine.
          </Callout>
        </div>
      </div>
    </div>;
}